import React from 'react'

import { ZariContext } from '_zari/context'

import { TreeView, Portlet, Button, Col, Row } from '_zari/core'

import { ZrSwal } from 'utils'

import * as CuentaContableCrud from 'services/cuentaContable.service'

import { DownloadAxiosFile } from 'utils'

import BuscadorInput from './buscador'
import * as Modals from './modals'

import './index.scss'

const portletBreadcrumbs = [{ name: 'Plan Contable' }]

const ArbolCuentaContablePage = () => {
  const __zariContext = React.useContext(ZariContext)

  const [cuentasContables, setCuentasContables] = React.useState([])
  const [agregarButtonDisabled, setAgregarButtonDisabled] = React.useState(false)
  const [editarButtonDisabled, setEditarButtonDisabled] = React.useState(false)
  const [eliminarButtonDisabled, setEliminarButtonDisabled] = React.useState(false)
  const [mostrarNuevaCuentaContableModal, setMostrarNuevaCuentaContableModal] = React.useState(false)
  const [mostrarEditarCuentaContableModal, setMostrarEditarCuentaContableModal] = React.useState(false)
  const [mostrarImportarCuentasContablesModal, setMostrarImportarCuentasContablesModal] = React.useState(false)

  const textoBuscadoRef = React.useRef('')
  const crearData = React.useRef(null)
  const editarData = React.useRef(null)
  const selectedId = React.useRef(null)
  const selectedItemData = React.useRef(null)

  const reloadTree = React.useCallback(() => {
    CuentaContableCrud.getCuentasContables(textoBuscadoRef.current).then(({ data }) => {
      setCuentasContables(data)
      setAgregarButtonDisabled(false)
      setEditarButtonDisabled(false)
      setEliminarButtonDisabled(false)
    })
  }, [])

  const getSelectedIdInfo = React.useCallback(() => {
    const nodeInfo = cuentasContables.find(x => x._id === selectedId.current)
    selectedItemData.current = nodeInfo
  }, [cuentasContables])

  const onSelectNode = React.useCallback(itemInfo => {
    selectedId.current = itemInfo
  }, [])

  const clickAgregarHandler = React.useCallback(() => {
    getSelectedIdInfo()

    CuentaContableCrud.getCuentaContable(selectedItemData.current._id)
      .then(({ data }) => {
        crearData.current = {
          ...data,
          ...selectedItemData.current
        }

        setMostrarNuevaCuentaContableModal(true)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext, getSelectedIdInfo])

  const clickAgregarCuentaPrincipal = React.useCallback(() => {
    crearData.current = {}
    setMostrarNuevaCuentaContableModal(true)
  }, [])

  const clickEditarHandler = React.useCallback(() => {
    getSelectedIdInfo()

    CuentaContableCrud.getCuentaContable(selectedItemData.current._id)
      .then(({ data }) => {
        editarData.current = {
          ...data,
          _id: selectedItemData.current._id
        }

        if (data.prefijoCodigoCuenta) {
          editarData.current.sufijoCodigoCuenta = data.codigoCuenta.split(data.prefijoCodigoCuenta)[1]
        } else {
          editarData.current.prefijoCodigoCuenta = data.codigoCuenta
        }
        setMostrarEditarCuentaContableModal(true)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext, getSelectedIdInfo])

  const clickEliminarItemHandler = React.useCallback(() => {
    getSelectedIdInfo()

    if (selectedItemData.current.childs && selectedItemData.current.childs.length > 0) {
      __zariContext.snack('error', 'La cuenta contable tiene cuentas asignadas.')
      return
    }

    ZrSwal.showAlert({
      mensaje: 'Seguro que desea eliminar el registro?',
      tipo: 'error',
      onSuccess: () => {
        CuentaContableCrud.eliminarCuentaContable(selectedItemData.current._id)
          .then(() => {
            reloadTree()
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      },
      showCerrar: true
    })
  }, [__zariContext, reloadTree, getSelectedIdInfo])

  const clickImportarHandler = React.useCallback(() => {
    setMostrarImportarCuentasContablesModal(true)
  }, [])

  const clickExportarHandler = React.useCallback(() => {
    CuentaContableCrud.exportarExcel()
      .then(({ data, headers }) => {
        DownloadAxiosFile.downloadAsBlob(data, headers)
        __zariContext.snack('success', 'Excel de cuentas contables descargado con exito')
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  const buscarPorFiltro = React.useCallback(
    busqueda => {
      setCuentasContables([])

      if (!busqueda) {
        __zariContext.snack('error', 'Ingresar texto para busqueda.')
        return
      }

      textoBuscadoRef.current = busqueda
      CuentaContableCrud.getCuentasContables(textoBuscadoRef.current).then(({ data }) => {
        if (data.length === 0) {
          __zariContext.toast('warning', 'No se encontraron cuentas contables con los filtros ingresados.')
        }
        setCuentasContables(data)
      })
    },
    [__zariContext]
  )

  const clickCopiarPlanAnoAnterior = React.useCallback(() => {
    ZrSwal.showAlert({
      mensaje: `Se eliminará el plan contable del año de trabajo actual.
        El cambio se realiza a nivel general y no a nivel de compañia.
        Seguro que desea copiar el plan del año anterior?`,
      tipo: 'info',
      onSuccess: () => {
        CuentaContableCrud.copiarPlanAnoAnterior()
          .then(() => {
            __zariContext.toast('success', 'Se ha duplicado el plan contable.')
            reloadTree()
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      },
      showCerrar: true
    })
  }, [__zariContext, reloadTree])

  return (
    <>
      <Portlet>
        <Portlet.Header
          title='Plan Contable'
          breadcrumbs={portletBreadcrumbs}
          buttons={[{ name: 'Copiar plan año anterior', onClick: clickCopiarPlanAnoAnterior }]}
        />
        <Portlet.Body className='cuentas_contables_arbol'>
          <Row className='row_buttons_wrapper'>
            <Col xs={3} sm={6} className='col_import_buttons_wrapper'>
              <Button onClick={clickImportarHandler}>Importar</Button>
              <Button outline onClick={clickExportarHandler}>Exportar</Button>
            </Col>
            <Col xs={9} sm={6} className='col_crud_buttons_wrapper'>
              <Button outline onClick={clickAgregarCuentaPrincipal} disabled={agregarButtonDisabled}>
                Agregar cuenta principal
              </Button>
              <Button onClick={clickAgregarHandler} disabled={agregarButtonDisabled}>
                Agregar
              </Button>
              <Button onClick={clickEditarHandler} disabled={editarButtonDisabled}>
                Editar
              </Button>
              <Button onClick={clickEliminarItemHandler} disabled={eliminarButtonDisabled}>
                Eliminar
              </Button>
            </Col>
          </Row>
          <Row className='zari-slim-form'>
            <Col md={6}>
              <BuscadorInput buscarPorFiltro={buscarPorFiltro} />
            </Col>
          </Row>
          <div className='treeview_wrapper'>
            <TreeView items={cuentasContables} onSelect={onSelectNode} />
          </div>
        </Portlet.Body>
      </Portlet>
      {mostrarNuevaCuentaContableModal && (
        <Modals.NuevaCuentaContableModal
          modalData={crearData.current}
          handleClose={e => setMostrarNuevaCuentaContableModal(false)}
          show={mostrarNuevaCuentaContableModal}
          reloadTree={reloadTree}
        />
      )}
      {mostrarEditarCuentaContableModal && (
        <Modals.EditarCuentaContableModal
          modalData={editarData.current}
          handleClose={e => setMostrarEditarCuentaContableModal(false)}
          show={mostrarEditarCuentaContableModal}
          reloadTree={reloadTree}
        />
      )}
      {mostrarImportarCuentasContablesModal && (
        <Modals.ImportarCuentasContablesModal
          handleClose={e => setMostrarImportarCuentasContablesModal(false)}
          show={mostrarImportarCuentasContablesModal}
          reloadTree={reloadTree}
        />
      )}
    </>
  )
}

export default React.memo(ArbolCuentaContablePage)
